import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, } from 'chart.js';
import { Line } from 'react-chartjs-2';
import { generateUrl } from '../../../Utilities/api';
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend);
function scoreToBand(score) {
    if (score < 40)
        return 'red';
    if (score < 70)
        return 'yellow';
    return 'green';
}
// ── Constants ──────────────────────────────────────────────────────────────
const BRAND = {
    blue: '#00537e',
    blueDeep: '#004360',
    tint: '#EEF4F7',
    red: '#f93f43',
    yellow: '#efe300',
    green: '#138f38',
};
const BAND_COLORS = {
    red: BRAND.red,
    yellow: BRAND.yellow,
    green: BRAND.green,
};
const BAND_LABELS = {
    red: 'Verbesserungspotenzial',
    yellow: 'Ausbaufähig',
    green: 'Gut',
};
const CATEGORY_LABELS = {
    __overall__: 'Gesamtscore',
    kraft: 'Kraft',
    beweglichkeit: 'Beweglichkeit',
    gleichgewicht: 'Gleichgewicht',
    koerperzusammensetzung: 'Körperzusammensetzung',
    schmerzfreiheit: 'Schmerzfreiheit',
    stressfreiheit: 'Stressfreiheit',
    energie: 'Energie',
    ernaehrung: 'Ernährung',
    mental: 'Mental',
    environment: 'Umfeld',
    regeneration: 'Regeneration',
    training: 'Training',
};
const IST_ZUSTAND_CATEGORIES = [
    'kraft', 'beweglichkeit', 'gleichgewicht',
    'koerperzusammensetzung', 'schmerzfreiheit', 'stressfreiheit', 'energie',
];
const VERHALTEN_CATEGORIES = ['ernaehrung', 'mental', 'environment', 'regeneration', 'training'];
function fmtDateTime(iso) {
    return new Date(iso).toLocaleDateString('de-DE', {
        year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
}
function fmtDate(iso) {
    return new Date(iso).toLocaleDateString('de-DE', { year: 'numeric', month: 'short', day: 'numeric' });
}
// ── ScoreRing ──────────────────────────────────────────────────────────────
function ScoreRing({ score, size = 120, strokeWidth = 10, label }) {
    const color = BAND_COLORS[scoreToBand(score)];
    const r = (size - strokeWidth) / 2;
    const circ = 2 * Math.PI * r;
    const dash = (score / 100) * circ;
    return (<div className="relative inline-flex items-center justify-center flex-col">
            <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }} aria-hidden="true">
                <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#e5e7eb" strokeWidth={strokeWidth}/>
                <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={strokeWidth} strokeDasharray={`${dash} ${circ - dash}`} strokeLinecap="round" style={{ transition: 'stroke-dashoffset 0.6s ease' }}/>
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="font-bold leading-none" style={{ fontSize: size * 0.22, color: '#111827' }}>{score}</span>
                {label !== undefined && (<span className="text-xs text-gray-500 mt-0.5 text-center px-1 leading-tight">{label}</span>)}
            </div>
        </div>);
}
// ── ProgressBar ────────────────────────────────────────────────────────────
function ProgressBar({ score, height = 6 }) {
    return (<div className="w-full rounded-full overflow-hidden bg-gray-100" style={{ height }}>
            <div className="h-full rounded-full transition-all" style={{ width: `${score}%`, backgroundColor: BAND_COLORS[scoreToBand(score)] }}/>
        </div>);
}
// ── Shimmer placeholders ───────────────────────────────────────────────────
function Shimmer({ className }) {
    return <div className={`animate-pulse bg-gray-100 rounded ${className ?? ''}`}/>;
}
function ScoreRingPlaceholder({ size = 130 }) {
    return (<div className="relative flex items-center justify-center" style={{ width: size, height: size }}>
            <svg width={size} height={size} aria-hidden="true">
                <circle cx={size / 2} cy={size / 2} r={(size - 13) / 2} fill="none" stroke="#e5e7eb" strokeWidth={13}/>
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-1.5">
                <Shimmer className="w-12 h-7"/>
                <Shimmer className="w-16 h-3"/>
            </div>
        </div>);
}
function IstZustandRowPlaceholder({ category }) {
    return (<div className="rounded-lg p-3" style={{ borderLeft: '3px solid transparent' }}>
            <div className="flex items-center gap-2 mb-1.5">
                <Shimmer className="w-2.5 h-2.5 rounded-full"/>
                <span className="text-sm font-medium text-gray-300">{CATEGORY_LABELS[category] ?? category}</span>
            </div>
            <div className="w-full rounded-full overflow-hidden bg-gray-100" style={{ height: 6 }}/>
        </div>);
}
function VerhaltenTilePlaceholder({ category }) {
    return (<div className="flex flex-col items-center gap-2 rounded-xl p-4" style={{ border: '1px solid #e5e7eb' }}>
            <div className="relative flex items-center justify-center" style={{ width: 80, height: 80 }}>
                <svg width={80} height={80} aria-hidden="true">
                    <circle cx={40} cy={40} r={36} fill="none" stroke="#e5e7eb" strokeWidth={8}/>
                </svg>
                <Shimmer className="absolute w-8 h-5"/>
            </div>
            <span className="text-xs font-semibold text-gray-300">{CATEGORY_LABELS[category] ?? category}</span>
        </div>);
}
function HebelCardPlaceholder() {
    return (<div className="bg-white rounded-2xl p-5 flex flex-col items-center justify-center gap-3 border-l-4" style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.10)', borderLeftColor: '#d1dfe8' }}>
            <Shimmer className="h-2.5 w-24"/>
            <ScoreRingPlaceholder size={120}/>
            <Shimmer className="h-4 w-28"/>
        </div>);
}
// ── IstZustandRow ──────────────────────────────────────────────────────────
function IstZustandRow({ category, entry, isFocus, onSelect }) {
    return (<button type="button" onClick={onSelect} className="w-full text-left rounded-lg p-3 transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-1" style={isFocus ? { borderLeft: `3px solid ${BRAND.blue}`, backgroundColor: BRAND.tint, paddingLeft: 12 } : { borderLeft: '3px solid transparent' }}>
            <div className="flex items-center gap-2 mb-1.5">
                <span className="inline-block rounded-full w-2.5 h-2.5 flex-shrink-0" style={{ backgroundColor: BAND_COLORS[scoreToBand(entry.score)] }}/>
                <span className="text-sm font-medium text-gray-800 flex-1">{CATEGORY_LABELS[category] ?? category}</span>
                {entry.trainerScore !== undefined && (<span className="inline-flex items-center text-xs font-semibold px-1.5 py-0.5 rounded" style={{ backgroundColor: BRAND.tint, color: BRAND.blue }}>Gemessen</span>)}
                <span className="text-sm font-bold ml-1" style={{ color: BAND_COLORS[scoreToBand(entry.score)] }}>{entry.score}</span>
            </div>
            <ProgressBar score={entry.score} height={6}/>
        </button>);
}
// ── VerhaltenTile ──────────────────────────────────────────────────────────
function VerhaltenTile({ category, entry, isFocus, onSelect }) {
    return (<button type="button" onClick={onSelect} className="flex flex-col items-center gap-2 bg-white rounded-2xl p-4 transition-shadow hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-offset-1 relative" style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.10)', ...(isFocus ? { outline: `2px solid ${BRAND.blue}` } : {}) }}>
            <ScoreRing score={entry.score} size={80} strokeWidth={8}/>
            <div className="text-center">
                <div className="flex items-center gap-1 justify-center">
                    <span className="inline-block rounded-full w-2.5 h-2.5 flex-shrink-0" style={{ backgroundColor: BAND_COLORS[scoreToBand(entry.score)] }}/>
                    <span className="text-xs font-semibold text-gray-700">{CATEGORY_LABELS[category] ?? category}</span>
                </div>
            </div>
        </button>);
}
// ── HebelCard ──────────────────────────────────────────────────────────────
function HebelCard({ leverCategory, measurementTargets, categoryScores }) {
    if (leverCategory === null) {
        return (<div className="bg-white rounded-2xl p-5 flex flex-col items-center justify-center gap-2 border-l-4" style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.10)', borderLeftColor: BRAND.blue }}>
                <p className="text-xs text-gray-400">Kein Hebel ermittelt</p>
            </div>);
    }
    const entry = categoryScores[leverCategory];
    const relevantTargets = measurementTargets.filter(t => t.currentScore < 100);
    return (<div className="bg-white rounded-2xl p-5 flex flex-col items-center justify-between gap-4 border-l-4" style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.10)', borderLeftColor: BRAND.blue }}>
            <div className="flex flex-col items-center gap-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Hebel-Kategorie</p>
                {entry !== undefined && <ScoreRing score={entry.score} size={120} strokeWidth={10}/>}
                <p className="text-sm font-bold text-center" style={{ color: BRAND.blueDeep }}>
                    {CATEGORY_LABELS[leverCategory] ?? leverCategory}
                </p>
            </div>
            {relevantTargets.length > 0 && (<div className="w-full flex flex-col gap-1 border-t border-gray-100 pt-3">
                    <p className="text-xs text-gray-400 mb-0.5">Messziele</p>
                    {relevantTargets.slice(0, 3).map(t => (<div key={t.categoryKey} className="flex items-baseline gap-1 text-xs">
                            <span className="text-gray-500 truncate flex-1">{t.label}</span>
                            <span className="font-bold shrink-0">{t.currentValue.toFixed(1)}</span>
                            {t.targetValue !== null ? (<>
                                    <span className="text-gray-300">→</span>
                                    <span className="text-green-600 font-bold shrink-0">{t.targetValue.toFixed(1)}</span>
                                </>) : (<span className="text-green-600">✓</span>)}
                            {t.unit !== null && <span className="text-gray-400">{t.unit}</span>}
                        </div>))}
                </div>)}
        </div>);
}
// ── HistoryChart ───────────────────────────────────────────────────────────
function HistoryChart({ category, history }) {
    const sorted = [...history].reverse();
    const isOverall = category === '__overall__';
    const labels = sorted.map(r => fmtDate(r.date));
    const overallScores = sorted.map(r => r.overallScore);
    const catScores = isOverall ? null : sorted.map(r => r.categoryScores[category] ?? null);
    const datasets = isOverall
        ? [{
                label: 'Gesamtscore',
                data: overallScores,
                borderColor: BRAND.blue,
                backgroundColor: `${BRAND.blue}18`,
                fill: false,
                tension: 0.3,
                pointRadius: 5,
                pointHoverRadius: 7,
                pointBackgroundColor: overallScores.map(s => BAND_COLORS[scoreToBand(s)]),
                pointBorderColor: '#fff',
                pointBorderWidth: 2,
                borderWidth: 2.5,
                spanGaps: true,
            }]
        : [
            {
                label: CATEGORY_LABELS[category] ?? category,
                data: catScores,
                borderColor: BRAND.blue,
                backgroundColor: `${BRAND.blue}18`,
                fill: false,
                tension: 0.3,
                pointRadius: 5,
                pointHoverRadius: 7,
                pointBackgroundColor: catScores.map(s => s !== null ? BAND_COLORS[scoreToBand(s)] : BRAND.blue),
                pointBorderColor: '#fff',
                pointBorderWidth: 2,
                borderWidth: 2.5,
                spanGaps: true,
            },
            {
                label: 'Gesamtscore',
                data: overallScores,
                borderColor: '#9ca3af',
                backgroundColor: 'transparent',
                fill: false,
                tension: 0.3,
                pointRadius: 3,
                pointHoverRadius: 5,
                pointBackgroundColor: '#9ca3af',
                pointBorderColor: '#fff',
                pointBorderWidth: 1.5,
                borderWidth: 1.5,
                // @ts-ignore
                borderDash: [4, 3],
            },
        ];
    return (<div>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3">Verlauf</h3>
            <div style={{ height: 180 }}>
                <Line data={{ labels, datasets }} options={{
            responsive: true,
            maintainAspectRatio: false,
            scales: {
                y: { min: 0, max: 100, ticks: { stepSize: 25, color: '#9ca3af', font: { size: 10 } }, grid: { color: '#f3f4f6' }, border: { display: false } },
                x: { ticks: { color: '#9ca3af', font: { size: 10 } }, grid: { display: false }, border: { display: false } },
            },
            plugins: {
                legend: { display: true, position: 'bottom', labels: { boxWidth: 12, font: { size: 10 }, color: '#6b7280', padding: 12 } },
                tooltip: { callbacks: { label: (ctx) => `${ctx.dataset.label ?? ''}: ${ctx.parsed.y ?? '–'}` } },
            },
        }}/>
            </div>
        </div>);
}
// ── CategoryDetailPanel ────────────────────────────────────────────────────
function CategoryDetailPanel({ category, entry, measurementTargets, history, onClose }) {
    const isOverall = category === '__overall__';
    const band = scoreToBand(entry.score);
    const text = !isOverall ? (entry.interpretation?.customer ?? null) : null;
    return (<div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.4)' }} onClick={e => { if (e.target === e.currentTarget)
        onClose(); }}>
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto" style={{ borderTop: `4px solid ${BRAND.blue}` }}>
                <div className="flex items-center justify-between p-5 pb-3">
                    <div className="flex items-center gap-3">
                        <ScoreRing score={entry.score} size={64} strokeWidth={7}/>
                        <div>
                            <h2 className="text-lg font-bold" style={{ color: BRAND.blueDeep }}>
                                {CATEGORY_LABELS[category] ?? category}
                            </h2>
                            <div className="flex items-center gap-1.5 mt-0.5">
                                <span className="inline-block rounded-full w-2.5 h-2.5" style={{ backgroundColor: BAND_COLORS[band] }}/>
                                <span className="text-sm text-gray-500">{BAND_LABELS[band]}</span>
                                {!isOverall && entry.trainerScore !== undefined && (<span className="text-xs font-semibold px-1.5 py-0.5 rounded" style={{ backgroundColor: BRAND.tint, color: BRAND.blue }}>Gemessen</span>)}
                            </div>
                        </div>
                    </div>
                    <button type="button" onClick={onClose} className="p-2 rounded-full text-gray-400 hover:bg-gray-100 transition-colors" aria-label="Schließen">
                        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                            <path d="M14 4L4 14M4 4l10 10"/>
                        </svg>
                    </button>
                </div>

                <div className="px-5 pb-5 flex flex-col gap-4">
                    {text !== null && (<div className="rounded-lg p-3" style={{ backgroundColor: BRAND.tint }}>
                            <p className="text-sm text-gray-700 leading-relaxed">{text}</p>
                        </div>)}

                    {!isOverall && entry.fragebogenScore !== undefined && entry.trainerScore !== undefined && (<div className="grid grid-cols-2 gap-3">
                            <div className="rounded-lg border border-gray-100 p-3 text-center">
                                <p className="text-xs text-gray-400 mb-1">Fragebogen</p>
                                <p className="text-xl font-bold" style={{ color: BRAND.blue }}>{entry.fragebogenScore}</p>
                                <ProgressBar score={entry.fragebogenScore} height={4}/>
                            </div>
                            <div className="rounded-lg border border-gray-100 p-3 text-center">
                                <p className="text-xs text-gray-400 mb-1">Trainermessung</p>
                                <p className="text-xl font-bold" style={{ color: BRAND.blue }}>{entry.trainerScore}</p>
                                <ProgressBar score={entry.trainerScore} height={4}/>
                                {entry.isPartial === true && <p className="text-xs text-amber-500 mt-1">Teilmessung</p>}
                            </div>
                        </div>)}

                    {measurementTargets.length > 0 && (<div>
                            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2">Messwerte &amp; Ziele</h3>
                            <div className="flex flex-col gap-2">
                                {measurementTargets.map(t => (<div key={t.categoryKey} className="rounded-lg border border-gray-100 p-3">
                                        <div className="flex items-center justify-between mb-1">
                                            <span className="text-sm font-medium text-gray-700">{t.label}</span>
                                            <span className="inline-block rounded-full w-2 h-2" style={{ backgroundColor: BAND_COLORS[scoreToBand(t.currentScore)] }}/>
                                        </div>
                                        <div className="flex items-baseline gap-1 text-sm">
                                            <span className="font-bold">{t.currentValue.toFixed(1)}</span>
                                            {t.unit !== null && <span className="text-gray-400 text-xs">{t.unit}</span>}
                                            {t.targetValue !== null ? (<>
                                                    <span className="text-gray-300 mx-1">→</span>
                                                    <span className="text-green-600 font-bold">{t.targetValue.toFixed(1)}</span>
                                                    {t.unit !== null && <span className="text-gray-400 text-xs">{t.unit}</span>}
                                                    <span className="text-xs text-gray-400 ml-1">für Score {t.targetScore}</span>
                                                </>) : (<span className="text-green-600 text-xs ml-1">✓ Ziel erreicht</span>)}
                                        </div>
                                    </div>))}
                            </div>
                        </div>)}

                    {history.length > 1 && <HistoryChart category={category} history={history}/>}
                </div>
            </div>
        </div>);
}
const HealthscoreCockpitV2 = ({ testUrl }) => {
    const [mode, setMode] = useState('bericht');
    const [records, setRecords] = useState([]);
    const [reportSummaries, setReportSummaries] = useState([]);
    const [selectedToken, setSelectedToken] = useState(null);
    const [selectedUuid, setSelectedUuid] = useState(null);
    const [fullReport, setFullReport] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isReportLoading, setIsReportLoading] = useState(false);
    const [hasError, setHasError] = useState(false);
    useEffect(() => {
        const fetchAll = async () => {
            try {
                const [recRes, repRes] = await Promise.all([
                    fetch(generateUrl('sitzkrieger_v2_myareaapi_healthscore_v2_history'), { credentials: 'same-origin' }),
                    fetch(generateUrl('sitzkrieger_v2_myareaapi_healthscore_v2_reports'), { credentials: 'same-origin' }),
                ]);
                const recs = recRes.ok ? await recRes.json() : [];
                const reps = repRes.ok ? await repRes.json() : [];
                setRecords(recs);
                setReportSummaries(reps);
                if (reps.length > 0) {
                    setMode('bericht');
                    setSelectedUuid(reps[0].uuid);
                }
                else if (recs.length > 0) {
                    setMode('healthscore');
                    setSelectedToken(recs[0].token);
                }
            }
            catch {
                setHasError(true);
            }
            finally {
                setIsLoading(false);
            }
        };
        void fetchAll();
    }, []);
    useEffect(() => {
        if (selectedUuid === null) {
            setFullReport(null);
            return;
        }
        setIsReportLoading(true);
        void fetch(generateUrl('sitzkrieger_v2_myareaapi_healthscore_v2_report', { uuid: selectedUuid }), { credentials: 'same-origin' })
            .then(r => r.ok ? r.json() : null)
            .then(data => { setFullReport(data); setIsReportLoading(false); })
            .catch(() => { setFullReport(null); setIsReportLoading(false); });
    }, [selectedUuid]);
    const handleModeChange = useCallback((newMode) => {
        setMode(newMode);
        setSelectedCategory(null);
        setFullReport(null);
        if (newMode === 'bericht') {
            setSelectedUuid(reportSummaries.length > 0 ? reportSummaries[0].uuid : null);
        }
        else {
            setSelectedToken(records.length > 0 ? records[0].token : null);
        }
    }, [reportSummaries, records]);
    const handleCategorySelect = useCallback((cat) => {
        setSelectedCategory(prev => prev === cat ? null : cat);
    }, []);
    const currentRecord = mode === 'healthscore' && selectedToken !== null
        ? records.find(r => r.token === selectedToken) ?? null
        : null;
    const categoryScores = mode === 'bericht' && fullReport !== null
        ? fullReport.categoryScores
        : currentRecord !== null
            ? Object.fromEntries(Object.entries(currentRecord.groupScores).map(([k, v]) => [k, { score: Math.round(v), source: 'questionnaire' }]))
            : {};
    const overallScore = mode === 'bericht' && fullReport !== null
        ? fullReport.overallScore
        : currentRecord !== null
            ? currentRecord.score
            : null;
    const computedFokusIst = () => {
        let lowest = 101;
        let cat = null;
        for (const key of IST_ZUSTAND_CATEGORIES) {
            const e = categoryScores[key];
            if (e !== undefined && e.score < lowest) {
                lowest = e.score;
                cat = key;
            }
        }
        return cat;
    };
    const computedFokusV = () => {
        let lowest = 101;
        let cat = null;
        for (const key of VERHALTEN_CATEGORIES) {
            const e = categoryScores[key];
            if (e !== undefined && e.score < lowest) {
                lowest = e.score;
                cat = key;
            }
        }
        return cat;
    };
    const fokusIstZustand = mode === 'bericht' && fullReport !== null
        ? fullReport.fokusIstZustand
        : computedFokusIst();
    const fokusVerhalten = mode === 'bericht' && fullReport !== null
        ? fullReport.fokusVerhalten
        : computedFokusV();
    const measurementTargets = mode === 'bericht' && fullReport !== null ? fullReport.measurementTargets : [];
    const istZustandPresent = IST_ZUSTAND_CATEGORIES.filter(c => categoryScores[c] !== undefined);
    const verhaltenPresent = VERHALTEN_CATEGORIES.filter(c => categoryScores[c] !== undefined);
    const hasData = Object.keys(categoryScores).length > 0;
    const showSkeleton = isLoading || isReportLoading;
    const historyPoints = mode === 'bericht'
        ? reportSummaries.map(r => ({ date: r.generatedAt, overallScore: r.overallScore, categoryScores: r.categoryScores }))
        : records.map(r => ({
            date: r.finishedAt,
            overallScore: r.score,
            categoryScores: Object.fromEntries(Object.entries(r.groupScores).map(([k, v]) => [k, Math.round(v)])),
        }));
    const selectedEntry = selectedCategory === '__overall__' && overallScore !== null
        ? { score: overallScore, source: 'overall' }
        : selectedCategory !== null
            ? categoryScores[selectedCategory]
            : undefined;
    const filteredTargets = selectedEntry?.includedKeys !== undefined
        ? measurementTargets.filter(t => selectedEntry.includedKeys.includes(t.categoryKey))
        : [];
    if (hasError)
        return <></>;
    return (<div className="flex flex-col gap-6">
            {/* Mode toggle + Dropdown + CTA */}
            <div className="flex items-center gap-3 flex-wrap">
                <div className="flex rounded-lg overflow-hidden border border-gray-200">
                    {['healthscore', 'bericht'].map(m => (<button key={m} type="button" onClick={() => handleModeChange(m)} className="px-3 py-1.5 text-xs font-medium transition-colors" style={mode === m
                ? { backgroundColor: BRAND.blue, color: '#fff' }
                : { backgroundColor: '#fff', color: BRAND.blueDeep }}>
                            {m === 'healthscore' ? 'Healthscore' : 'Vollständiger Bericht'}
                        </button>))}
                </div>

                {!isLoading && mode === 'bericht' && reportSummaries.length > 0 && (<select value={selectedUuid ?? ''} onChange={e => { setSelectedUuid(e.target.value); setSelectedCategory(null); }} className="text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2" style={{ minWidth: 220 }}>
                        {reportSummaries.map(r => (<option key={r.uuid} value={r.uuid}>
                                {fmtDateTime(r.generatedAt)} — Score {r.overallScore}
                                {r.dataSources.includes('trainer') ? ' (Messung)' : ' (Fragebogen)'}
                            </option>))}
                    </select>)}
                {!isLoading && mode === 'healthscore' && records.length > 0 && (<select value={selectedToken ?? ''} onChange={e => { setSelectedToken(e.target.value); setSelectedCategory(null); }} className="text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2" style={{ minWidth: 220 }}>
                        {records.map(r => (<option key={r.token} value={r.token}>
                                {fmtDateTime(r.finishedAt)} — Score {r.score}
                            </option>))}
                    </select>)}
                {!isLoading && ((mode === 'bericht' && reportSummaries.length === 0) || (mode === 'healthscore' && records.length === 0)) && (<span className="text-sm text-gray-400 italic">Noch kein Eintrag vorhanden</span>)}

                <a href={testUrl} className="px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors ml-auto shrink-0" style={{ backgroundColor: BRAND.blue }}>
                    Neuen Test starten
                </a>
            </div>

            {/* Top row: Score | Ist-Zustand | Hebel */}
            <div className="grid gap-4" style={{ gridTemplateColumns: '1fr 2fr 1fr' }}>
                <button type="button" onClick={() => { if (hasData && overallScore !== null)
        handleCategorySelect('__overall__'); }} disabled={!hasData || overallScore === null} className="bg-white rounded-2xl p-5 flex flex-col items-center justify-center gap-3 transition-shadow focus:outline-none focus:ring-2 focus:ring-offset-2" style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.10)', cursor: hasData && overallScore !== null ? 'pointer' : 'default' }}>
                    {showSkeleton ? (<ScoreRingPlaceholder size={130}/>) : hasData && overallScore !== null ? (<ScoreRing score={overallScore} size={130} strokeWidth={13} label="Gesamtscore"/>) : (<>
                            <ScoreRingPlaceholder size={130}/>
                            <p className="text-xs text-gray-400 text-center">Noch kein Test</p>
                        </>)}
                </button>

                <div className="bg-white rounded-2xl p-5" style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.10)' }}>
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1 px-1">Ist-Zustand</h3>
                    <div className="flex flex-col gap-1">
                        {showSkeleton || !hasData
            ? IST_ZUSTAND_CATEGORIES.map(cat => <IstZustandRowPlaceholder key={cat} category={cat}/>)
            : istZustandPresent.map(cat => (<IstZustandRow key={cat} category={cat} entry={categoryScores[cat]} isFocus={fokusIstZustand === cat} onSelect={() => handleCategorySelect(cat)}/>))}
                    </div>
                </div>

                {showSkeleton ? (<HebelCardPlaceholder />) : !hasData ? (<div className="bg-white rounded-2xl flex flex-col items-center justify-center p-5 gap-3" style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.10)' }}>
                        <p className="text-sm text-gray-500 text-center max-w-xs">Mach deinen ersten Healthscore-Test.</p>
                        <a href={testUrl} className="text-sm font-bold px-4 py-2 rounded-lg text-white" style={{ backgroundColor: BRAND.blue }}>Test starten</a>
                    </div>) : (<HebelCard leverCategory={fokusIstZustand} measurementTargets={measurementTargets} categoryScores={categoryScores}/>)}
            </div>

            {/* Verhalten tiles */}
            <div>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3 px-1">Verhalten</h3>
                <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(5, 1fr)' }}>
                    {showSkeleton || !hasData
            ? VERHALTEN_CATEGORIES.map(cat => <VerhaltenTilePlaceholder key={cat} category={cat}/>)
            : verhaltenPresent.map(cat => (<VerhaltenTile key={cat} category={cat} entry={categoryScores[cat]} isFocus={fokusVerhalten === cat} onSelect={() => handleCategorySelect(cat)}/>))}
                </div>
            </div>

            {selectedCategory !== null && selectedEntry !== undefined && (<CategoryDetailPanel category={selectedCategory} entry={selectedEntry} measurementTargets={filteredTargets} history={historyPoints} onClose={() => setSelectedCategory(null)}/>)}
        </div>);
};
HealthscoreCockpitV2.displayName = 'HealthscoreCockpitV2';
export default HealthscoreCockpitV2;
